import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CopyHashButton from "./CopyHashButton.tsx";
import { tableBodyCellSx, tableHeadCellSx } from "@/theme/eventsTokens.ts";
import { alertSummary, NOT_SENT, recipientLabel, shortHash } from "@/utils/alertCopy.ts";
import { formatDateTime, formatTime } from "@/utils/formatDateTime.ts";
import { relativeTime } from "@/utils/relativeTime.ts";
import type { Alert } from "@/types";

const MAX_COUNTRIES = 3;
const HEADERS = ["When", "What happened", "Account", "Sources", "Notified"];
const WIDTHS = ["12%", "36%", "15%", "13%", "24%"];

function sourcesText(alert: Alert): string {
  const shown = alert.countries.slice(0, MAX_COUNTRIES).join(" ");
  const more = alert.countries.length > MAX_COUNTRIES ? ` +${alert.countries.length - MAX_COUNTRIES}` : "";
  return `${alert.source_count.toLocaleString("en-US")}${shown ? ` · ${shown}${more}` : ""}`;
}

function NotifiedCell({ alert }: { readonly alert: Alert }) {
  if (alert.notifications.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {NOT_SENT}
      </Typography>
    );
  }
  const firstSent = Math.min(...alert.notifications.map((n) => n.at));
  return (
    <Typography variant="body2" component="span">
      {alert.notifications.map((n, i) => (
        <Box component="span" key={`${n.channel}:${n.recipient}:${n.at}:${i}`}>
          {i > 0 && ", "}
          {n.delivered ? (
            <span>{recipientLabel(n)}</span>
          ) : (
            <Tooltip title={n.error ?? "Not delivered"}>
              <Box component="span" aria-label={`${recipientLabel(n)}: not delivered`} sx={{ color: "error.main" }}>
                {recipientLabel(n)}
              </Box>
            </Tooltip>
          )}
        </Box>
      ))}
      {` · ${formatTime(firstSent)}`}
    </Typography>
  );
}

function AlertRow({ alert, onOpen }: { readonly alert: Alert; readonly onOpen: (id: string) => void }) {
  return (
    <TableRow hover onClick={() => onOpen(alert.id)} sx={{ cursor: "pointer" }}>
      <TableCell sx={tableBodyCellSx}>
        <Tooltip title={formatDateTime(alert.last_seen)}>
          <span>{relativeTime(alert.last_seen)}</span>
        </Tooltip>
      </TableCell>
      <TableCell sx={tableBodyCellSx}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          sx={{ textAlign: "left", color: "inherit" }}
          onClick={(event) => {
            event.stopPropagation();
            onOpen(alert.id);
          }}
        >
          {alertSummary(alert)}
        </Link>
      </TableCell>
      <TableCell sx={tableBodyCellSx}>
        <Box component="span" sx={{ fontFamily: "monospace" }}>
          {shortHash(alert.account_hash)}
        </Box>
        <CopyHashButton hash={alert.account_hash} />
      </TableCell>
      <TableCell sx={tableBodyCellSx}>{sourcesText(alert)}</TableCell>
      <TableCell sx={tableBodyCellSx}>
        <NotifiedCell alert={alert} />
      </TableCell>
    </TableRow>
  );
}

export default function AlertsTable({
  rows,
  onOpen,
}: {
  readonly rows: readonly Alert[];
  readonly onOpen: (id: string) => void;
}) {
  return (
    <TableContainer>
      <Table size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
        <colgroup>
          {WIDTHS.map((width, i) => (
            <col key={HEADERS[i]} style={{ width }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            {HEADERS.map((header) => (
              <TableCell key={header} sx={tableHeadCellSx}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((alert) => (
            <AlertRow key={alert.id} alert={alert} onOpen={onOpen} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
